import React, { useState, useCallback, useEffect } from 'react';
import { Tabs, Spin } from 'antd';
import './PWP255Form.css';

import { usePWP255Mode } from './hooks/usePWP255Mode';
import { usePWP255Record } from './hooks/usePWP255Record';
import { usePWP255Navigation } from './hooks/usePWP255Navigation';
import ActionBar from './components/ActionBar';
import Tab1BasicInfo from './components/Tab1BasicInfo';
import Tab2Details from './components/Tab2Details';
import type { FP620, FP621 } from '../../services/pwp255Api';

const { TabPane } = Tabs;

const EMPTY_MASTER: Partial<FP620> = {
  fp620_01: '',
  fp620_10: '',
  fp620_11: new Date().toISOString(),
  fp620_13: '',
  fp620_14: '',
  fp620_141: '',
  fp620_15: '',
  fp620_16: '',
  fp620_17: '',
  fp620_18: 0,
  fp620_22: 0.9144,
  fp620_23: 0,
  fp620_24: 0,
  fp620_25: 1,
  fp620_28: 1,
  fp620_29: '',
  fp620_91: '',
  fp620_93: '',
};

const PWP255Form: React.FC = () => {
  const { mode, toView, toAdd, toEdit, isEditing } = usePWP255Mode();
  const { record, loading, load, save, setRecord } = usePWP255Record();
  const {
    hasPrev, hasNext, fetchList,
    goFirst, goLast, goPrev, goNext, setCurrentById,
  } = usePWP255Navigation();

  const [formData, setFormData] = useState<Partial<FP620>>(EMPTY_MASTER);
  const [details, setDetails] = useState<Partial<FP621>[]>([]);
  const [activeTab, setActiveTab] = useState('1');

  // Sync record to form state
  useEffect(() => {
    if (record) {
      setFormData({ ...record });
      setDetails(record.details ? record.details.map(d => ({ ...d })) : []);
    }
  }, [record]);

  const loadRecord = useCallback(async (id: string) => {
    const result = await load(id);
    if (result) {
      setCurrentById(id);
    }
    return result;
  }, [load, setCurrentById]);

  // Navigation handlers
  const handleFirst = useCallback(async () => {
    const id = goFirst();
    if (id) await loadRecord(id);
  }, [goFirst, loadRecord]);

  const handlePrev = useCallback(async () => {
    const id = goPrev();
    if (id) await loadRecord(id);
  }, [goPrev, loadRecord]);

  const handleNext = useCallback(async () => {
    const id = goNext();
    if (id) await loadRecord(id);
  }, [goNext, loadRecord]);

  const handleLast = useCallback(async () => {
    const id = goLast();
    if (id) await loadRecord(id);
  }, [goLast, loadRecord]);

  // Mode handlers
  const handleAdd = useCallback(() => {
    setFormData({ ...EMPTY_MASTER, fp620_11: new Date().toISOString() });
    setDetails([]);
    setRecord(null);
    toAdd();
    setActiveTab('1');
  }, [toAdd, setRecord]);

  const handleEdit = useCallback(() => {
    if (!record) return;
    setFormData({ ...record });
    setDetails(record.details ? record.details.map(d => ({ ...d })) : []);
    toEdit();
  }, [record, toEdit]);

  const handleCancel = useCallback(() => {
    if (record) {
      setFormData({ ...record });
      setDetails(record.details ? record.details.map(d => ({ ...d })) : []);
    } else {
      setFormData({ ...EMPTY_MASTER });
      setDetails([]);
    }
    toView();
  }, [record, toView]);

  // Save handler (T-F01: use response to update state; T-F09: { master, details })
  const handleSave = useCallback(async () => {
    const isNew = mode === 'add';
    const result = await save(formData, details, isNew);
    if (result) {
      await fetchList(1);
      toView();
    }
  }, [formData, details, mode, save, fetchList, toView]);

  // Field change handler (T-F03: parseFloat for numbers in components)
  const handleFieldChange = useCallback((field: keyof FP620, value: string | number | null) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  // Details change handler: keep fp620_18 in sync
  const handleDetailsChange = useCallback((next: Partial<FP621>[]) => {
    setDetails(next);
    setFormData(prev => ({ ...prev, fp620_18: next.length }));
  }, []);

  return (
    <div className="pwp255-form">
      <h2 style={{ margin: '0 0 8px 0', fontSize: 16 }}>
        PACKING LIST 客戶資料（貨櫃）
      </h2>

      <ActionBar
        mode={mode}
        hasRecord={!!record}
        hasPrev={hasPrev}
        hasNext={hasNext}
        loading={loading}
        onFirst={handleFirst}
        onPrev={handlePrev}
        onNext={handleNext}
        onLast={handleLast}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onSave={handleSave}
        onCancel={handleCancel}
      />

      <Spin spinning={loading}>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="基本資料" key="1">
            <Tab1BasicInfo
              formData={formData}
              isEditing={isEditing}
              mode={mode}
              onChange={handleFieldChange}
            />
          </TabPane>
          <TabPane tab="明細" key="2">
            <Tab2Details
              formData={formData}
              details={details}
              shippingUnit={formData.fp620_25 || 1}
              isEditing={isEditing}
              onChange={handleDetailsChange}
              onFieldChange={handleFieldChange}
            />
          </TabPane>
        </Tabs>
      </Spin>
    </div>
  );
};

export default PWP255Form;
